import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import { DOCUMENT_FIELD_DEFS } from '../../utils/personFormAdapter';
import { api } from '../../services/api';
import { useUserPrefs } from '../context/UserPrefs';

const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024; // 10MB

// Niveles/categorías que puede cubrir la Tarjeta CONTE de un colaborador
// (independiente del archivo adjunto docTarjetaConte) — selección múltiple.
const CONTE_CATEGORY_OPTIONS = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'AUX DE INGENIERIA'];

// Categorías de licencia de conducción — selección múltiple, cada una con
// su propia fecha de vencimiento (ver formData.drivingLicenses).
const DRIVING_LICENSE_CATEGORY_OPTIONS = ['A1', 'A2', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'];

// Tarjetas y consejos profesionales — selección múltiple, mismo patrón que
// Categorías Tarjeta CONTE.
const PROFESSIONAL_COUNCIL_OPTIONS = [
  'CONALTEL (tecnólogos electricistas)',
  'COPNIA (ingenieros)',
  'Consejo Nacional de Ingenieros Electricistas y Electromecánicos',
  'Consejo Nacional de Trabajo Social (trabajador social)',
  'COLPSIC (psicólogos)',
  'Resolución por Secretaría Departamental (SST)',
];

// Vacunas con conteo de dosis aplicadas (independiente del archivo adjunto
// docCertificadoVacunas). Clave interna -> etiqueta visible.
const VACCINE_DOSE_FIELDS = [
  { key: 'hepatitisA', label: 'Hepatitis A' },
  { key: 'hepatitisB', label: 'Hepatitis B' },
  { key: 'tetano', label: 'Tétano' },
  { key: 'fiebreAmarilla', label: 'Fiebre amarilla' },
  { key: 'covid19', label: 'COVID-19' },
];
const emptyVaccineDoses = () => Object.fromEntries(VACCINE_DOSE_FIELDS.map((v) => [v.key, '']));

// Experiencia laboral previa (alimentada por Gestión Humana a partir de la
// HV física entregada por el colaborador) — empresa, cargo y periodo.
const emptyWorkExperience = () => ({ company: '', position: '', startDate: '', endDate: '' });

// Contactos de emergencia (primer y segundo respondiente).
const emptyResponder = () => ({ nombre: '', celular: '', parentesco: '', direccion: '' });
const RESPONDER_FIELDS = [
  { key: 'nombre', label: 'Nombre' },
  { key: 'celular', label: 'Celular' },
  { key: 'parentesco', label: 'Parentesco' },
  { key: 'direccion', label: 'Dirección' },
];

// Un solo archivo por tipo de documento (cédula, licencia, certificados,
// etc.) — mismo patrón que la foto o la firma: se reemplaza al subir uno
// nuevo. Componente compartido para no repetir el mismo bloque 30 veces.
function DocumentFileField({ label, value, onChange }) {
  const isExisting = typeof value === 'string' && /^https?:\/\//i.test(value);
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <input
        type="file"
        accept="image/*,application/pdf"
        onChange={(e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          if (typeof file.size === 'number' && file.size > MAX_DOCUMENT_BYTES) {
            window.alert('El archivo supera el tamaño máximo permitido (10MB).');
            try { e.target.value = ''; } catch (_) {}
            return;
          }
          const reader = new FileReader();
          reader.onloadend = () => onChange(reader.result);
          reader.readAsDataURL(file);
        }}
        className="w-full border rounded px-2 py-1.5 text-sm"
      />
      {value && (
        <div className="mt-1 flex items-center gap-2 text-xs">
          {isExisting ? (
            <a href={value} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">Ver archivo cargado</a>
          ) : (
            <span className="text-gray-600">Archivo listo para guardar</span>
          )}
          <button type="button" onClick={() => onChange('')} className="text-red-600 hover:text-red-700">Quitar</button>
        </div>
      )}
    </div>
  );
}

export const PersonForm = ({ person, onSave, onCancel, showExtraHRFields = false, activePeople = [] }) => {
  // Salario: visible y editable solo para Contabilidad, Recursos Humanos y
  // quien recibe novedades de RRHH (mismo criterio que people.php).
  const { currentUser } = useUserPrefs();
  const canEditSalary = ['Contabilidad', 'Recursos Humanos'].includes(String(currentUser?.role || '').trim())
    || !!currentUser?.noveltyHrCanReceive;
  const [formData, setFormData] = useState({
    photo: '',
    id: '',
    name: '',
    idIssuePlace: '',
    category: '',
    appliesExtras: '',
    conteCategories: [],
    professionalCouncils: [],
    vaccineDoses: emptyVaccineDoses(),
    firstResponder: emptyResponder(),
    secondResponder: emptyResponder(),
    position: '',
    operationalRole: '',
    area: '',
    clientValidation: 'Pendiente',
    phone: '',
    civilStatus: '',
    email: '',
    eps: '',
    compensationFund: '',
    drivingLicense: '', // compatibilidad: categorías unidas por coma
    drivingLicenseExpiry: '', // compatibilidad: vencimiento más próximo
    drivingLicenses: [], // nuevo: [{ category, expiry }] — varias licencias, cada una con su vencimiento
    pensionFund: '',
    afp: '',
    bloodType: '',
    allergies: '',
    allergyType: '',
    birthCity: '',
    birthDate: '',
    address: '',
    neighborhood: '',
    municipality: '',
    department: '',
    hireDate: '',
    education: '',
    workExperience: [], // experiencia laboral previa reportada por el colaborador: [{ company, position, startDate, endDate }]
    contractDate: '',
    contractNumber: '',
    currentContractModality: '',
    immediateBossId: '',
    otrosiDate: '', // compatibilidad: último otrosí
    otrosiDates: [], // nuevo: múltiples fechas de otrosí
    arlRiskPercent: '',
    emoDate: '',
    nextEmoDate: '',
    salary: '',
    ...Object.fromEntries(DOCUMENT_FIELD_DEFS.map((d) => [d.key, ''])),
    status: 'Activo',
    signature: '',
    contractEndDate: ''
  });
  const [otrosiInput, setOtrosiInput] = useState('');
  const [weInput, setWeInput] = useState(emptyWorkExperience());
  const [operationalRoleCatalog, setOperationalRoleCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.people.operationalRoleCatalog();
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
        if (!cancelled) setOperationalRoleCatalog(rows.filter(Boolean));
      } catch (_) {
        if (!cancelled) setOperationalRoleCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (person) {
      // Conservar valores por defecto para nuevas claves y sobreescribir con la persona
      setFormData((prev) => {
        const incoming = { ...prev, ...person };
        // El servidor lo entrega como "2500000.00": se guarda en pesos enteros.
        const salaryNum = Number(incoming.salary);
        incoming.salary = canEditSalary && incoming.salary != null && incoming.salary !== '' && Number.isFinite(salaryNum) && salaryNum > 0
          ? String(Math.round(salaryNum))
          : '';
        // Normalizar múltiples otrosí
        if (!Array.isArray(incoming.otrosiDates)) {
          incoming.otrosiDates = incoming.otrosiDate ? [incoming.otrosiDate] : [];
        }
        // Normalizar múltiples licencias de conducción: si la persona se
        // guardó antes de este cambio (solo drivingLicense/
        // drivingLicenseExpiry como valores únicos), migrar a la lista.
        if (!Array.isArray(incoming.drivingLicenses)) {
          incoming.drivingLicenses = incoming.drivingLicense
            ? [{ category: incoming.drivingLicense, expiry: incoming.drivingLicenseExpiry || '' }]
            : [];
        }
        // Asegurar campo de finalización de contrato
        if (!incoming.contractEndDate) {
          incoming.contractEndDate = '';
        }
        // Experiencia laboral previa: campo nuevo, sin migración necesaria
        if (!Array.isArray(incoming.workExperience)) {
          incoming.workExperience = [];
        }
        // Completar dosis de vacunas que aún no existían cuando se guardó la persona
        incoming.vaccineDoses = { ...emptyVaccineDoses(), ...(incoming.vaccineDoses || {}) };
        incoming.firstResponder = { ...emptyResponder(), ...(incoming.firstResponder || {}) };
        incoming.secondResponder = { ...emptyResponder(), ...(incoming.secondResponder || {}) };
        return incoming;
      });
    }
  }, [person, canEditSalary]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.status === 'Inactivo') {
      const d = (formData.contractEndDate || '').trim();
      if (!d) {
        window.alert('La fecha de finalización de contrato es obligatoria al marcar Inactivo.');
        return;
      }
    }
    // Si el usuario llenó una fecha de otrosí pero no alcanzó a pulsar
    // "Agregar", igual se incluye en el guardado (no se pierde el dato).
    const pendingOtrosi = String(otrosiInput || '').trim() ? [otrosiInput] : [];
    const normalizedOtrosi = [
      ...(Array.isArray(formData.otrosiDates) ? formData.otrosiDates : []),
      ...pendingOtrosi,
    ].filter(Boolean);
    const normalizedLicenses = Array.isArray(formData.drivingLicenses)
      ? formData.drivingLicenses.filter((d) => d && String(d.category || '').trim())
      : [];
    const licenseExpiries = normalizedLicenses.map((d) => d.expiry).filter(Boolean).sort();
    // Igual que otrosí: si llenó la experiencia laboral en el formulario
    // pero no pulsó "Agregar experiencia", se incluye igual al guardar.
    const pendingWorkExperience = String(weInput?.company || '').trim() ? [{ ...weInput }] : [];
    const normalizedWorkExperience = [
      ...(Array.isArray(formData.workExperience) ? formData.workExperience : []),
      ...pendingWorkExperience,
    ].filter((w) => w && String(w.company || '').trim());
    const payload = {
      ...formData,
      allergyType: String(formData.allergies || '').trim().toLowerCase() === 'si' ? (formData.allergyType || '') : '',
      otrosiDates: normalizedOtrosi,
      // Mantener compatibilidad: usar el último otrosí como valor singular
      otrosiDate: normalizedOtrosi.length ? normalizedOtrosi[normalizedOtrosi.length - 1] : (formData.otrosiDate || ''),
      drivingLicenses: normalizedLicenses,
      // Mantener compatibilidad: categorías unidas por coma y el vencimiento más próximo
      drivingLicense: normalizedLicenses.map((d) => d.category).join(', '),
      drivingLicenseExpiry: licenseExpiries[0] || '',
      workExperience: normalizedWorkExperience,
    };
    onSave(payload);
  };

  const addOtrosiDate = () => {
    if (!otrosiInput) return;
    setFormData((prev) => ({
      ...prev,
      otrosiDates: [...(Array.isArray(prev.otrosiDates) ? prev.otrosiDates : []), otrosiInput],
    }));
    setOtrosiInput('');
  };

  const removeOtrosiDate = (index) => {
    setFormData((prev) => ({
      ...prev,
      otrosiDates: (prev.otrosiDates || []).filter((_, i) => i !== index),
    }));
  };

  const addWorkExperience = () => {
    if (!String(weInput.company || '').trim()) return;
    setFormData((prev) => ({
      ...prev,
      workExperience: [...(Array.isArray(prev.workExperience) ? prev.workExperience : []), weInput],
    }));
    setWeInput(emptyWorkExperience());
  };

  const removeWorkExperience = (index) => {
    setFormData((prev) => ({
      ...prev,
      workExperience: (prev.workExperience || []).filter((_, i) => i !== index),
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Foto del colaborador</label>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) { setFormData((prev) => ({ ...prev, photo: '' })); return; }
              const MAX_BYTES = 8 * 1024 * 1024; // 8MB
              if (typeof file.size === 'number' && file.size > MAX_BYTES) {
                window.alert('La foto supera el tamaño máximo permitido (8MB).');
                return;
              }
              const reader = new FileReader();
              reader.onloadend = () => setFormData((prev) => ({ ...prev, photo: reader.result }));
              reader.readAsDataURL(file);
            }}
            className="w-full border rounded px-3 py-2"
          />
          {formData.photo && (
            <div className="mt-2 p-2 border rounded bg-gray-50 flex items-center gap-3">
              <img src={formData.photo} alt="Foto" className="h-20 w-20 object-cover rounded" />
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, photo: '' }))}
                className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
              >
                Quitar foto
              </button>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Documento de Identidad *</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            value={formData.id}
            onChange={(e) => {
              const v = (e.target.value || '').replace(/\D/g, '');
              setFormData((prev) => ({ ...prev, id: v }));
            }}
            className="w-full border rounded px-3 py-2"
            disabled={!!person}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nombre Completo *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>
        {showExtraHRFields && (
          <div>
            <label className="block text-sm font-medium mb-1">Lugar expedición de la cédula</label>
            <input
              type="text"
              value={formData.idIssuePlace}
              onChange={(e) => setFormData({ ...formData, idIssuePlace: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        )}
        {showExtraHRFields && (
          <div>
            <label className="block text-sm font-medium mb-1">Categoría</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full border rounded px-3 py-2 bg-white"
            >
              <option value="">Seleccione...</option>
              <option value="Administrativo">Administrativo</option>
              <option value="Operativo">Operativo</option>
            </select>
          </div>
        )}
        {showExtraHRFields && (
          <div>
            <label className="block text-sm font-medium mb-1">¿Aplica extras?</label>
            <select
              value={formData.appliesExtras}
              onChange={(e) => setFormData({ ...formData, appliesExtras: e.target.value })}
              className="w-full border rounded px-3 py-2 bg-white"
            >
              <option value="">Seleccione...</option>
              <option value="Si">Sí</option>
              <option value="No">No</option>
            </select>
          </div>
        )}
        {showExtraHRFields && (
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Categorías Tarjeta CONTE</label>
            <div className="flex flex-wrap gap-3 border rounded px-3 py-2">
              {CONTE_CATEGORY_OPTIONS.map((opt) => {
                const checked = Array.isArray(formData.conteCategories) && formData.conteCategories.includes(opt);
                return (
                  <label key={opt} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const current = Array.isArray(prev.conteCategories) ? prev.conteCategories : [];
                          const next = e.target.checked
                            ? [...current, opt]
                            : current.filter((c) => c !== opt);
                          return { ...prev, conteCategories: next };
                        });
                      }}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {showExtraHRFields && (
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Tarjetas y consejos profesionales</label>
            <div className="flex flex-wrap gap-3 border rounded px-3 py-2">
              {PROFESSIONAL_COUNCIL_OPTIONS.map((opt) => {
                const checked = Array.isArray(formData.professionalCouncils) && formData.professionalCouncils.includes(opt);
                return (
                  <label key={opt} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const current = Array.isArray(prev.professionalCouncils) ? prev.professionalCouncils : [];
                          const next = e.target.checked
                            ? [...current, opt]
                            : current.filter((c) => c !== opt);
                          return { ...prev, professionalCouncils: next };
                        });
                      }}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {showExtraHRFields && (
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Vacunas (dosis aplicadas)</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 border rounded px-3 py-2">
              {VACCINE_DOSE_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600 mb-1">{label}</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.vaccineDoses?.[key] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        vaccineDoses: { ...(prev.vaccineDoses || {}), [key]: v },
                      }));
                    }}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {showExtraHRFields && (
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Primer respondiente</label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border rounded px-3 py-2">
              {RESPONDER_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600 mb-1">{label}</label>
                  <input
                    type="text"
                    value={formData.firstResponder?.[key] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        firstResponder: { ...(prev.firstResponder || {}), [key]: v },
                      }));
                    }}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {showExtraHRFields && (
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Segundo respondiente</label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border rounded px-3 py-2">
              {RESPONDER_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600 mb-1">{label}</label>
                  <input
                    type="text"
                    value={formData.secondResponder?.[key] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        secondResponder: { ...(prev.secondResponder || {}), [key]: v },
                      }));
                    }}
                    className="w-full border rounded px-2 py-1.5 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Cargo</label>
          <input
            type="text"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            className="w-full border rounded px-3 py-2"
            placeholder="Puesto laboral"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Rol operativo (perfil de campo)</label>
          <select
            value={formData.operationalRole || ''}
            onChange={(e) => setFormData({ ...formData, operationalRole: e.target.value })}
            className="w-full border rounded px-3 py-2 bg-white"
          >
            <option value="">Sin asignar</option>
            {formData.operationalRole
              && !operationalRoleCatalog.includes(formData.operationalRole)
              && (
                <option value={formData.operationalRole}>{formData.operationalRole}</option>
              )}
            {operationalRoleCatalog.map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Distinto del cargo. No modifica permisos de sistema en Administración TI.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Proyecto</label>
          <select
            value={formData.area}
            onChange={(e) => setFormData({ ...formData, area: e.target.value })}
            className="w-full border rounded px-3 py-2 bg-white"
          >
            <option value="">Seleccione...</option>
            <option value="Transversal">Transversal</option>
            <option value="Desarrollo">Desarrollo</option>
            <option value="Linea Viva">Linea Viva</option>
            <option value="Mantenimiento">Mantenimiento</option>
            <option value="EMCALI">EMCALI</option>
            <option value="Solartech">Solartech</option>
            {formData.area && !['Transversal','Desarrollo','Linea Viva','Mantenimiento','EMCALI','Solartech'].includes(formData.area) && (
              <option value={formData.area}>{`Otro: ${formData.area}`}</option>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Validación Cliente</label>
          <select
            value={formData.clientValidation}
            onChange={(e) => setFormData({ ...formData, clientValidation: e.target.value })}
            className="w-full border rounded px-3 py-2"
          >
            <option value="Aprobado">Aprobado</option>
            <option value="Pendiente">Pendiente</option>
            <option value="No aplica">No aplica</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Teléfono</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Estado civil</label>
          <select
            value={formData.civilStatus}
            onChange={(e) => setFormData({ ...formData, civilStatus: e.target.value })}
            className="w-full border rounded px-3 py-2 bg-white"
          >
            <option value="">Seleccione...</option>
            <option value="Soltero">Soltero</option>
            <option value="Casado">Casado</option>
            <option value="Union libre">Union libre</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">EPS</label>
          <input
            type="text"
            value={formData.eps}
            onChange={(e) => setFormData({ ...formData, eps: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        {showExtraHRFields && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Caja de compensación</label>
              <input
                type="text"
                value={formData.compensationFund}
                onChange={(e) => setFormData({ ...formData, compensationFund: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Licencia de conducción</label>
              <div className="flex flex-wrap gap-3 border rounded px-3 py-2">
                {DRIVING_LICENSE_CATEGORY_OPTIONS.map((cat) => {
                  const checked = Array.isArray(formData.drivingLicenses) && formData.drivingLicenses.some((d) => d.category === cat);
                  return (
                    <label key={cat} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const current = Array.isArray(prev.drivingLicenses) ? prev.drivingLicenses : [];
                            const next = e.target.checked
                              ? [...current, { category: cat, expiry: '' }]
                              : current.filter((d) => d.category !== cat);
                            return { ...prev, drivingLicenses: next };
                          });
                        }}
                      />
                      {cat}
                    </label>
                  );
                })}
              </div>
              {Array.isArray(formData.drivingLicenses) && formData.drivingLicenses.length > 0 && (
                <div className="mt-2 space-y-2">
                  {formData.drivingLicenses.map((d) => (
                    <div key={d.category} className="flex items-center gap-3">
                      <span className="w-9 text-sm font-semibold text-gray-700">{d.category}</span>
                      <label className="text-xs text-gray-500 whitespace-nowrap">Vence:</label>
                      <input
                        type="date"
                        value={d.expiry || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            drivingLicenses: (prev.drivingLicenses || []).map((x) => (x.category === d.category ? { ...x, expiry: value } : x)),
                          }));
                        }}
                        className="border rounded px-2 py-1.5 text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Fondo-Pensiones</label>
          <input
            type="text"
            value={formData.pensionFund}
            onChange={(e) => setFormData({ ...formData, pensionFund: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de sangre</label>
          <input
            type="text"
            value={formData.bloodType}
            onChange={(e) => setFormData({ ...formData, bloodType: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        {showExtraHRFields && (
          <div>
            <label className="block text-sm font-medium mb-1">Sufre de alergias</label>
            <select
              value={formData.allergies}
              onChange={(e) => {
                const v = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  allergies: v,
                  allergyType: String(v || '').trim().toLowerCase() === 'si' ? (prev.allergyType || '') : '',
                }));
              }}
              className="w-full border rounded px-3 py-2 bg-white"
            >
              <option value="">Seleccione...</option>
              <option value="Si">Sí</option>
              <option value="No">No</option>
            </select>
          </div>
        )}
        {showExtraHRFields && String(formData.allergies || '').trim().toLowerCase() === 'si' && (
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de alergia</label>
            <input
              type="text"
              value={formData.allergyType}
              onChange={(e) => setFormData({ ...formData, allergyType: e.target.value })}
              className="w-full border rounded px-3 py-2"
              placeholder="Ej: Penicilina, polvo, alimentos..."
            />
          </div>
        )}
        {showExtraHRFields && (
          <div>
            <label className="block text-sm font-medium mb-1">Ciudad de nacimiento</label>
            <input
              type="text"
              value={formData.birthCity}
              onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Fecha nacimiento</label>
          <input
            type="date"
            value={formData.birthDate}
            onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        {showExtraHRFields && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Dirección</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="Calle 123 #45-67 Barrio..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Barrio</label>
              <input
                type="text"
                value={formData.neighborhood}
                onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Municipio</label>
              <input
                type="text"
                value={formData.municipality}
                onChange={(e) => setFormData({ ...formData, municipality: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Departamento</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha de ingreso</label>
              <input
                type="date"
                value={formData.hireDate}
                onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Formación</label>
              <textarea
                value={formData.education}
                onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows="3"
                placeholder="Títulos, cursos, certificaciones relevantes..."
              ></textarea>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Experiencia laboral</label>
              <p className="text-xs text-gray-500 mb-2">
                Basada en la hoja de vida entregada por el colaborador: empresa, cargo y tiempo trabajado.
              </p>
              <div className="space-y-2">
                {(formData.workExperience || []).map((w, idx) => (
                  <div key={`we-${idx}`} className="flex items-center justify-between gap-2 px-3 py-2 border rounded bg-white text-sm">
                    <div>
                      <div className="font-medium">{w.company}{w.position ? ` — ${w.position}` : ''}</div>
                      <div className="text-xs text-gray-500">
                        {w.startDate || '¿?'} a {w.endDate || 'Actual'}
                      </div>
                    </div>
                    <button type="button" onClick={() => removeWorkExperience(idx)} className="text-red-600 hover:text-red-700 shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {Array.isArray(formData.workExperience) && formData.workExperience.length === 0 && (
                  <span className="text-xs text-gray-500">Sin registros</span>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded p-3 bg-gray-50">
                  <input
                    value={weInput.company}
                    onChange={(e) => setWeInput((prev) => ({ ...prev, company: e.target.value }))}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Empresa"
                  />
                  <input
                    value={weInput.position}
                    onChange={(e) => setWeInput((prev) => ({ ...prev, position: e.target.value }))}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Cargo"
                  />
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Fecha inicio</label>
                    <input
                      type="date"
                      value={weInput.startDate}
                      onChange={(e) => setWeInput((prev) => ({ ...prev, startDate: e.target.value }))}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Fecha final (vacío si es el empleo actual)</label>
                    <input
                      type="date"
                      value={weInput.endDate}
                      onChange={(e) => setWeInput((prev) => ({ ...prev, endDate: e.target.value }))}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addWorkExperience}
                    className="sm:col-span-2 px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                  >
                    Agregar experiencia
                  </button>
                </div>
              </div>
            </div>
            {/* Campos adicionales de contrato y seguridad social */}
            <div>
              <label className="block text-sm font-medium mb-1">Fecha contrato</label>
              <input
                type="date"
                value={formData.contractDate}
                onChange={(e) => setFormData({ ...formData, contractDate: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número de contrato</label>
              <input
                type="text"
                value={formData.contractNumber}
                onChange={(e) => setFormData({ ...formData, contractNumber: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Modalidad contrato actual</label>
              <input
                type="text"
                value={formData.currentContractModality}
                onChange={(e) => setFormData({ ...formData, currentContractModality: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="Ej: Término indefinido, Fijo, OPS..."
              />
            </div>
            {canEditSalary && (
              <div>
                <label className="block text-sm font-medium mb-1">Salario básico mensual</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.salary ? Number(String(formData.salary).replace(/\D/g, '')).toLocaleString('es-CO') : ''}
                  onChange={(e) => setFormData({ ...formData, salary: String(e.target.value || '').replace(/\D/g, '') })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Ej: 2.500.000"
                />
                <p className="text-xs text-gray-500 mt-1">Se usa para calcular el valor de las horas extra. Solo lo ven Contabilidad y Talento Humano.</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Jefe Inmediato</label>
              <select
                className="w-full border rounded px-3 py-2 bg-white"
                value={formData.immediateBossId || ''}
                onChange={(e) => setFormData({ ...formData, immediateBossId: e.target.value })}
              >
                <option value="">Seleccione...</option>
                {activePeople.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Fechas otrosí</label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {(formData.otrosiDates || []).map((d, idx) => (
                    <span key={`${d}-${idx}`} className="inline-flex items-center gap-2 px-2 py-1 border rounded bg-white text-sm">
                      <span>{d}</span>
                      <button type="button" onClick={() => removeOtrosiDate(idx)} className="text-red-600 hover:text-red-700">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  {Array.isArray(formData.otrosiDates) && formData.otrosiDates.length === 0 && (
                    <span className="text-xs text-gray-500">Sin registros</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={otrosiInput}
                    onChange={(e) => setOtrosiInput(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                  />
                  <button type="button" onClick={addOtrosiDate} className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm">
                    Agregar
                  </button>
                </div>
              </div>
            </div>
            {/* Campo AFP removido por redundante con Fondo-Pensiones; el backend acepta 'afp' como monto, pero el nombre del fondo se gestiona con 'pensionFund'. */}
            <div>
              <label className="block text-sm font-medium mb-1">Riesgo ARL %</label>
              <input
                type="number"
                step="0.01"
                value={formData.arlRiskPercent}
                onChange={(e) => setFormData({ ...formData, arlRiskPercent: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="%"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">EMO</label>
              <input
                type="date"
                value={formData.emoDate}
                onChange={(e) => setFormData({ ...formData, emoDate: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Próximo EMO</label>
              <input
                type="date"
                value={formData.nextEmoDate}
                onChange={(e) => setFormData({ ...formData, nextEmoDate: e.target.value })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">Documentos y certificados</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {DOCUMENT_FIELD_DEFS.map((d) => (
                  <DocumentFileField
                    key={d.key}
                    label={d.label}
                    value={formData[d.key]}
                    onChange={(val) => setFormData((prev) => ({ ...prev, [d.key]: val }))}
                  />
                ))}
              </div>
            </div>
          </>
        )}
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Adjuntar firma digital</label>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) { setFormData({ ...formData, signature: '' }); return; }
              const MAX_SIG_BYTES = 5 * 1024 * 1024; // 5MB
              if (typeof file.size === 'number' && file.size > MAX_SIG_BYTES) {
                window.alert('La firma supera el tamaño máximo permitido (5MB).');
                return;
              }
              const reader = new FileReader();
              reader.onloadend = () => {
                setFormData((prev) => ({ ...prev, signature: reader.result }));
              };
              reader.readAsDataURL(file);
            }}
            className="w-full border rounded px-3 py-2"
          />
          {formData.signature && (
            <div className="mt-2 p-2 border rounded bg-gray-50">
              <span className="text-xs text-gray-600">Vista previa:</span>
              <img src={formData.signature} alt="Firma" className="mt-1 h-16 object-contain" />
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Estado</label>
          <select
            value={formData.status}
            onChange={(e) => {
              const nextStatus = e.target.value;
              let nextEnd = formData.contractEndDate;
              if (nextStatus === 'Inactivo' && !nextEnd) {
                try { nextEnd = new Date().toISOString().split('T')[0]; } catch (_) { nextEnd = ''; }
              }
              setFormData({ ...formData, status: nextStatus, contractEndDate: nextEnd });
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option>Activo</option>
            <option>Inactivo</option>
          </select>
        </div>
        {formData.status === 'Inactivo' && (
          <div>
            <label className="block text-sm font-medium mb-1">Fecha finalización de contrato</label>
            <input
              type="date"
              value={formData.contractEndDate}
              onChange={(e) => setFormData({ ...formData, contractEndDate: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        )}
      </div>
      <div className="sticky bottom-0 bg-white pt-3 pb-3 border-t mt-6">
        <div className="flex gap-2">
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-600"
          >
            <Save size={18} /> Guardar
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-400"
          >
            <X size={18} /> Cancelar
          </button>
        </div>
      </div>
    </form>
  );
};
